import { useEffect, useMemo } from "react";

import { AssessmentStatusTable } from "./AssessmentStatusTable";
import { EmptyAssessmentDashboard } from "./EmptyAssessmentDashboard";
import { useFetchAssessmentBaseData, useFetchAssessmentData } from "./assessment-data";
import {
  AllAssessmentDashboardDataType,
  useAllAssessmentData,
  useOpenForSubmissionAssessmentData,
} from "./hooks";

import { AssessmentStatus } from "api/api-data-types";
import { t } from "i18n/i18n";
import { storeApi, useAppSelector } from "store/index";
import { selectCurrentSectionId } from "store/selectors";

type PartitionedRows = AllAssessmentDashboardDataType["allAssessmentsTableRows"];
type PartitionedCSV = AllAssessmentDashboardDataType["allAssessmentsCSVData"];

const partitionAssessmentData = (
  allAssessmentsTableRows: PartitionedRows,
  allAssessmentsCSVData: PartitionedCSV,
) => {
  const assessableRows: PartitionedRows = [];
  const assessableCSVRows: PartitionedCSV["rows"] = [];

  const assessedRows: PartitionedRows = [];
  const assessedCSVRows: PartitionedCSV["rows"] = [];

  allAssessmentsTableRows.forEach((row, i) => {
    const csvRow = allAssessmentsCSVData.rows[i];
    if (
      row.status === AssessmentStatus.ASSESSABLE &&
      !row.isAssessmentComplete &&
      !row.isMarkedComplete
    ) {
      assessableRows.push(row);
      assessableCSVRows.push(csvRow);
    } else {
      assessedRows.push(row);
      assessedCSVRows.push(csvRow);
    }
  });

  const { headers } = allAssessmentsCSVData;
  return {
    assessableRows,
    assessableCSVData: { headers, rows: assessableCSVRows },
    assessedRows,
    assessedCSVData: { headers, rows: assessedCSVRows },
  };
};

export const AllAssessmentDashboard = () => {
  const sectionId = useAppSelector(selectCurrentSectionId);

  const { allAssessmentsCSVData, allAssessmentsTableRows } = useAllAssessmentData();

  const {
    allAssessmentsCSVData: openForSubmissionsCSVData,
    allAssessmentsTableRows: openForSubmissionsRows,
  } = useOpenForSubmissionAssessmentData();

  useFetchAssessmentBaseData();
  useFetchAssessmentData();

  useEffect(() => {
    if (sectionId) {
      storeApi.page_outcomes.list({ section_id: sectionId });
      storeApi.page_group_users.list({ section_id: sectionId });
    }
  }, [sectionId]);

  const { assessableRows, assessableCSVData, assessedRows, assessedCSVData } = useMemo(
    () => partitionAssessmentData(allAssessmentsTableRows, allAssessmentsCSVData),
    [allAssessmentsTableRows, allAssessmentsCSVData],
  );

  if (!sectionId) {
    return null;
  }

  const hasNoData = allAssessmentsTableRows.length === 0 && openForSubmissionsRows.length === 0;

  if (hasNoData) {
    return <EmptyAssessmentDashboard />;
  }

  return (
    <main className="assessment-dashboard">
      <AssessmentStatusTable
        allAssessmentsCSVData={assessableCSVData}
        allAssessmentsTableRows={assessableRows}
        emptyDescription={t("assessment_dashboard.all.empty_state.assessable")}
        sectionId={sectionId}
        title={t("assessment_dashboard.all.ready_for_assessment")}
      />

      <AssessmentStatusTable
        allAssessmentsCSVData={assessedCSVData}
        allAssessmentsTableRows={assessedRows}
        emptyDescription={t("assessment_dashboard.all.empty_state.assessed")}
        sectionId={sectionId}
        title={t("assessment_dashboard.all.assessed")}
      />

      <AssessmentStatusTable
        allAssessmentsCSVData={openForSubmissionsCSVData}
        allAssessmentsTableRows={openForSubmissionsRows}
        emptyDescription={t("assessment_dashboard.all.empty_state.open_submissions")}
        sectionId={sectionId}
        title={t("assessment_dashboard.all.open_for_submissions")}
        hideMarkCompleteMenu
      />
    </main>
  );
};
